import { baseApi } from '@/store/api/baseApi';
import type {
  BulkContactIdsPayload,
  BulkContactStatusPayload,
  ContactMessage,
  ContactListParams,
  PaginatedContacts,
  UpdateContactStatusPayload,
} from '@/types/contact';

function buildContactListQuery(params: ContactListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.desk?.trim()) searchParams.set('desk', params.desk.trim());
  return searchParams.toString();
}

export const contactsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getContacts: builder.query<PaginatedContacts, ContactListParams>({
      query: (params) => ({ url: `/contacts?${buildContactListQuery(params)}` }),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Contacts' as const, id })),
              { type: 'Contacts', id: 'LIST' },
            ]
          : [{ type: 'Contacts', id: 'LIST' }],
    }),
    getContact: builder.query<ContactMessage, string>({
      query: (id) => `/contacts/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Contacts', id }],
    }),
    updateContactStatus: builder.mutation<ContactMessage, { id: string; body: UpdateContactStatusPayload }>({
      query: ({ id, body }) => ({ url: `/contacts/update-status/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
      ],
    }),
    deleteContact: builder.mutation<void, string>({
      query: (id) => ({ url: `/contacts/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),
    bulkUpdateContactStatus: builder.mutation<{ count: number }, BulkContactStatusPayload>({
      query: (body) => ({ url: '/contacts/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),
    bulkDeleteContacts: builder.mutation<{ count: number }, BulkContactIdsPayload>({
      query: (body) => ({ url: '/contacts/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetContactsQuery,
  useGetContactQuery,
  useUpdateContactStatusMutation,
  useDeleteContactMutation,
  useBulkUpdateContactStatusMutation,
  useBulkDeleteContactsMutation,
} = contactsApi;
